import { all, call, fork, put, select, spawn } from "redux-saga/effects";

import { getAuthInstance } from "@tailormed/common-client/services/auth";
import { waitForLogin } from "@tailormed/common-client/store/saga/authSagas";
import initializeUserMetrics from "@tailormed/common-client/store/saga/services/userMetrics";
import { ApiNames } from "../../api/api";
import ErrorMessage from "../../component/customComponent/customMessages/ErrorMessage";
import { oktaConfig } from "../../constant/okta";
import { CACHE_KEYS, CACHE_TTL, getCache, setCache } from "../../utils/cache";
import ACTION from "../action";
import { selectUsers } from "../selector";
import { getUser, watchSignInActions } from "./authActions";
import { callApi } from "./networkActions";
import { actions } from "./sagaActions";

function* getUsers() {
  const cached = getCache(CACHE_KEYS.USERS, CACHE_TTL.USERS);
  if (cached) {
    yield put(ACTION.setUsers(cached.usersMap));
    yield put(ACTION.setAssignees(cached.assignees));
    return;
  }

  const result = yield callApi(ApiNames.UsersUnified);

  if (!result) {
    yield call(getUsersLegacy);
    return;
  }

  let usersMap = null;
  let assignees = null;

  if (result.all && result.all.users) {
    usersMap = result.all.users.reduce((obj, item) => Object.assign(obj, { [item.id]: item }), {});
    yield put(ACTION.setUsers(usersMap));
  } else {
    ErrorMessage("Failed to get users");
  }

  if (result.assignable && result.assignable.users) {
    assignees = result.assignable.users.reduce((obj, item) => Object.assign(obj, { [item.id]: item }), {});
    yield put(ACTION.setAssignees(assignees));
  } else {
    ErrorMessage("Failed to get assignees");
  }

  if (usersMap && assignees) {
    setCache(CACHE_KEYS.USERS, { usersMap, assignees });
  }
}

function* getUsersLegacy() {
  const [usersResult, assigneesResult] = yield all([call(callApi, ApiNames.Users), call(callApi, ApiNames.Assignees)]);

  let usersMap = null;
  let assignees = null;

  if (usersResult && usersResult.users) {
    usersMap = usersResult.users.reduce((obj, item) => Object.assign(obj, { [item.id]: item }), {});
    yield put(ACTION.setUsers(usersMap));
  } else {
    ErrorMessage("Failed to get users");
  }

  if (assigneesResult && assigneesResult.users) {
    assignees = assigneesResult.users.reduce((obj, item) => Object.assign(obj, { [item.id]: item }), {});
    yield put(ACTION.setAssignees(assignees));
  } else {
    ErrorMessage("Failed to get assignees");
  }

  if (usersMap && assignees) {
    setCache(CACHE_KEYS.USERS, { usersMap, assignees });
  }
}

function* getUserGroupsData() {
  const cached = getCache(CACHE_KEYS.USER_GROUPS, CACHE_TTL.USER_GROUPS);
  if (cached) {
    yield put(ACTION.setUserGroups(cached.userGroups));
    yield put(ACTION.setUserGroupsWithUsers(cached.userGroupsWithUsers));
    return;
  }

  const [groupsResult, mappingsResult] = yield all([
    call(callApi, ApiNames.UserGroups),
    call(callApi, ApiNames.UserGroupMappings)
  ]);

  const groups = groupsResult && groupsResult["user-groups"];
  if (!groups || !Array.isArray(groups)) {
    ErrorMessage("Failed to get user groups");
    return;
  }

  const userGroups = groups.reduce((obj, item) => Object.assign(obj, { [item.id]: item }), {});
  yield put(ACTION.setUserGroups(userGroups));

  if (mappingsResult && mappingsResult.mappings) {
    const users = yield select(selectUsers);
    const groupsWithUsers = groups.map((group) => ({
      id: group.id,
      name: group.name,
      users: (mappingsResult.mappings[group.id] || []).map((userId) => users && users[userId]).filter(Boolean)
    }));
    yield put(ACTION.setUserGroupsWithUsers(groupsWithUsers));
    setCache(CACHE_KEYS.USER_GROUPS, { userGroups, userGroupsWithUsers: groupsWithUsers });
  } else {
    ErrorMessage("Failed to get user group mappings");
  }
}

function* getSettings() {
  try {
    const settingsRes = yield callApi(ApiNames.Settings);
    if (settingsRes) {
      yield put(ACTION.setSettings(settingsRes.settings));
    }
  } catch (error) {
    ErrorMessage(`Failed to get settings: ${error}`);
  }
}

function* getProviders() {
  const cached = getCache(CACHE_KEYS.PROVIDERS, CACHE_TTL.PROVIDERS);
  if (cached) {
    for (const type of Object.keys(cached)) {
      yield put(ACTION.provider(type, cached[type]));
    }
    return;
  }

  const providerTypes = ["facility", "doctor"];
  const providerResults = yield all(providerTypes.map((type) => call(callApi, ApiNames.Providers, type)));

  const providersData = {};
  for (let i = 0; i < providerTypes.length; i++) {
    const result = providerResults[i];
    const type = providerTypes[i];
    if (result) {
      yield put(ACTION.provider(type, result.providers));
      providersData[type] = result.providers;
    } else {
      ErrorMessage(`Failed to get providers data`);
    }
  }
  if (Object.keys(providersData).length === providerTypes.length) {
    setCache(CACHE_KEYS.PROVIDERS, providersData);
  }
}

function* startup() {
  try {
    const oktaAuth = getAuthInstance(oktaConfig.redirectUri);
    const token = oktaAuth.getAccessToken();
    yield all([call(getUser), call(getSettings)]);
    yield spawn(initializeUserMetrics, { analytics: true, rum: true, token });
    yield spawn(getProviders);
    yield call(getUsers);
    yield spawn(getUserGroupsData);
  } catch (error) {
    ErrorMessage("Failed to initialize app. Please refresh the page.");
  }
}

function* rootSaga() {
  yield fork(watchSignInActions);
  yield waitForLogin();
  yield fork(startup);
  yield spawn(actions);
}

export { rootSaga };
